import { getUserInfo } from '_utils/localData'
import { useCallback, useMemo, useState } from 'react'
import { FiSettings } from 'react-icons/fi'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { useTable } from 'react-table'
import { useToasts } from 'react-toast-notifications'
import { Table } from 'reactstrap'
import uploadApi from '../../../api/upload'
import { useModal } from '../../../hooks/useModal'
import { deleteProductAction, editProductAction } from '../../../redux/modules/product'
import useOpenMenuSetting from '../hooks/useOpenMenuSetting'
import ModalOrderConfig from './OrderSetting'
import { MAX_SELECT_PRODUCT } from '../../../utils/constant'
import ModalSubItems from './ModalSubItems'
import ModalSplitCase from './ModalSplitCase'
import ModalEditProduct from './ModalEditProduct'
import Badge from './Badge'
import Progress from './Progress'
import ModalInfoSubItem from './ModalInfoSubItem'
import '../style.scss'

export default function ProductTable({
  items = [],
  selectedItems = [],
  onToggleSelect = () => {},
  onToggleSelectAll = () => {},
}) {
  const userInfo = getUserInfo()
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const now = useMemo(() => Date.now() / 1000, [])

  const [activeProductId, setActiveProductId] = useState(null)
  const activeRow = useMemo(
    () => items.find((item) => item.id === activeProductId) || null,
    [items, activeProductId]
  )

  const { activeMenuId, setActiveMenuId } = useOpenMenuSetting()
  const [previewImage, setPreviewImage] = useState({ id: null, url: null })

  const orderModal = useModal()
  const splitCaseModal = useModal()
  const subItemModal = useModal()
  const editModal = useModal()
  const infoSubItemModal = useModal()

  const handleOpenInfoSubItem = useCallback(
    (rowId) => {
      setActiveProductId(rowId)
      infoSubItemModal.openModal()
    },
    [infoSubItemModal.openModal]
  )

  const handleEditProduct = useCallback(
    (rowId) => {
      setActiveProductId(rowId)
      editModal.openModal()
      setActiveMenuId(null)
    },
    [editModal.openModal, setActiveMenuId]
  )

  const handleOrderConfig = useCallback(
    (rowId) => {
      setActiveProductId(rowId)
      orderModal.openModal()
      setActiveMenuId(null)
    },
    [orderModal.openModal, setActiveMenuId]
  )

  const handleSplitCase = useCallback(
    (rowId) => {
      setActiveProductId(rowId)
      splitCaseModal.openModal()
      setActiveMenuId(null)
    },
    [splitCaseModal.openModal, setActiveMenuId]
  )

  const handleAddSubItems = useCallback(
    (rowId) => {
      setActiveProductId(rowId)
      subItemModal.openModal()
      setActiveMenuId(null)
    },
    [subItemModal.openModal, setActiveMenuId]
  )

  const handleImageChange = useCallback(
    async (rowId, eventId, file) => {
      if (!file) return

      const localUrl = URL.createObjectURL(file)
      setPreviewImage({ id: rowId, url: localUrl })
      try {
        const { msgResp } = await uploadApi.uploadPhoto([file])
        const uploadedUrl = msgResp?.url

        if (!uploadedUrl) {
          addToast(msgResp, { appearance: 'error', autoDismiss: true })
          setPreviewImage({ id: null, url: null })
          return
        }

        await dispatch(editProductAction(rowId, eventId, { photoUrls: [uploadedUrl] }))
        addToast('Image updated successfully', { appearance: 'success', autoDismiss: true })
      } catch (error) {
        addToast(error, { appearance: 'error', autoDismiss: true })
        setPreviewImage({ id: null, url: null })
      }
    },
    [dispatch, addToast]
  )

  const handleDeleteItem = useCallback(
    async (row) => {
      if (row.createdUserId !== userInfo.id) {
        addToast('You are not the creator of this event.', {
          appearance: 'error',
          autoDismiss: true,
        })
        return
      }
      if (window.confirm('Delete this product?')) {
        try {
          const { msgResp } = await dispatch(deleteProductAction(row.id, row.eventId))
          addToast(msgResp?.message || 'Delete successfully', {
            appearance: 'success',
            autoDismiss: true,
          })
        } catch (error) {
          addToast(error?.msgResp || 'Delete failed', {
            appearance: 'error',
            autoDismiss: true,
          })
        }
      }
    },
    [userInfo.id, dispatch, addToast]
  )

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        className: 'text-center',
        style: { width: '30px' },
        Header: () => (
          <input
            className='product-checkbox'
            type='checkbox'
            checked={
              items.length > 0 && items.some((i) => selectedItems.some((s) => s.id === i.id))
            }
            onChange={() => onToggleSelectAll(items)}
          />
        ),
        Cell: ({ row }) => (
          <input
            type='checkbox'
            className='product-checkbox'
            disabled={
              selectedItems.length >= MAX_SELECT_PRODUCT &&
              !selectedItems.some((i) => i.id === row.original.id)
            }
            checked={selectedItems.some((i) => i.id === row.original.id)}
            onChange={() => onToggleSelect(row.original)}
          />
        ),
      },
      {
        id: 'image',
        style: { width: '60px' },
        Header: '',
        Cell: ({ row }) => (
          <div className='d-flex align-items-center gap-2'>
            <label className='upload-image-label'>
              <input
                type='file'
                accept='image/*'
                style={{ display: 'none' }}
                onChange={(e) =>
                  handleImageChange(row.original.id, row.original.eventId, e.target.files[0])
                }
              />
              <div className='position-relative'>
                {row.original.photoUrls?.[0] ||
                (previewImage.id === row.original.id && previewImage.url) ? (
                  <img
                    className='img-placeholder'
                    src={
                      (previewImage.id === row.original.id && previewImage.url) ||
                      row.original.photoUrls?.[0] ||
                      ''
                    }
                    alt='Product Image'
                  />
                ) : (
                  <div className='none-img-placeholder'>Product</div>
                )}
              </div>
            </label>
          </div>
        ),
      },
      {
        Header: 'Product Name',
        accessor: 'name',
        className: 'text-muted',
        Cell: ({ row }) => (
          <>
            <div className='product-name'>{row.original.name}</div>
            <div className='product-description'>{row.original.description}</div>
          </>
        ),
      },
      {
        Header: 'Event',
        accessor: 'eventName',
        className: 'text-muted',
        Cell: ({ row }) => (
          <>
            <div className='event-info-wrapper'>
              {row.original.eventId ? (
                <Link
                  to={`/event/detail?eid=${row.original.eventId}`}
                  className='limit-text event-name-link'
                >
                  {row.original.eventName}
                </Link>
              ) : (
                row.original.eventName
              )}
            </div>
            <Badge status={row.original.eventClosingTime > now ? 'open' : 'closed'} size='sm' />
          </>
        ),
      },
      {
        Header: 'Category',
        accessor: 'categoryName',
        className: 'text-muted',
        Cell: ({ row }) =>
          (row.original.categoryName ? (
            <Badge variant='category' size='sm'>
              {row.original.categoryName}
            </Badge>
          ) : (
            '—'
          )),
      },
      {
        Header: 'Price',
        accessor: 'price',
        className: 'text-muted',
        Cell: ({ row }) => (
          <>
            <div className='price-cell'>${row.original.price?.toFixed(2)}</div>
            <div className='price-sub'>Min order: {row.original.minOrderQty}</div>
          </>
        ),
      },
      {
        Header: 'Stock',
        accessor: 'inStock',
        className: 'text-muted',
        Cell: ({ row }) => <Progress value={row.original.inStock} max={row.original.maxQuantity} />,
      },
      {
        Header: 'Orders',
        accessor: 'orderCount',
        Cell: ({ row }) => (
          <div
            className={`order-count-cell ${row.original.orderCount > 0 ? 'active-count' : ''}`}
          >
            {row.original.orderCount}
          </div>
        ),
      },
      {
        Header: 'Added options',
        id: 'addedOptions',
        Cell: ({ row }) => (
          <AddedOptionInfo
            onOpenInfoSubItem={handleOpenInfoSubItem}
            row={row.original}
          />
        ),
      },
      {
        Header: '',
        id: 'actions',
        Cell: ({ row, index }) => (
          <div
            className={`settings-cell ${
              activeMenuId === (row.original.id || index) ? 'active' : ''
            }`}
          >
            {activeMenuId === (row.original.id || index) && (
              <MenuSubitem
                onEditProductClick={() => handleEditProduct(row.original.id)}
                onOrderConfigClick={() => handleOrderConfig(row.original.id)}
                onSplitCaseClick={() => handleSplitCase(row.original.id)}
                onAddSubItemsClick={() => handleAddSubItems(row.original.id)}
                onDeleteProductClick={() => {
                  handleDeleteItem(row.original)
                  setActiveMenuId(null)
                }}
              />
            )}
            <button
              type='button'
              className='settings-btn'
              onClick={(e) => {
                e.stopPropagation()
                setActiveMenuId(
                  activeMenuId === (row.original.id || index)
                    ? null
                    : row.original.id || index
                )
              }}
            >
              <FiSettings className='settings-btn-icon' />
              <span>Settings</span>
            </button>
          </div>
        ),
      },
    ],
    [
      items,
      selectedItems,
      onToggleSelect,
      onToggleSelectAll,
      previewImage,
      handleImageChange,
      now,
      handleOpenInfoSubItem,
      activeMenuId,
      setActiveMenuId,
      handleEditProduct,
      handleOrderConfig,
      handleSplitCase,
      handleAddSubItems,
      handleDeleteItem,
    ]
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: items,
  })

  return (
    <div className='table-wrapper mt-3'>
      <Table borderless className='custom-table mb-0' {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps()
            return (
              <tr key={headerGroupKey} {...headerGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key: headerKey, ...headerProps } = column.getHeaderProps()
                  return (
                    <th
                      key={headerKey}
                      {...headerProps}
                      className={column.className}
                      style={column.style}
                    >
                      {column.render('Header')}
                    </th>
                  )
                })}
              </tr>
            )
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className='text-center'>
                No data available
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              prepareRow(row)
              const { key: rowKey, ...rowProps } = row.getRowProps()
              return (
                <tr key={rowKey || row.original.id || index} {...rowProps}>
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps()
                    return (
                      <td
                        key={cellKey}
                        {...cellProps}
                        className={cell.column.className}
                      >
                        {cell.render('Cell', { index })}
                      </td>
                    )
                  })}
                </tr>
              )
            })
          )}
        </tbody>
      </Table>

      <ModalOrderConfig
        isOpen={orderModal.isOpen}
        toggle={orderModal.toggleModal}
        row={activeRow}
      />
      <ModalSplitCase
        isOpen={splitCaseModal.isOpen}
        toggle={splitCaseModal.toggleModal}
        row={activeRow}
      />
      <ModalSubItems
        isOpen={subItemModal.isOpen}
        toggle={subItemModal.toggleModal}
        row={activeRow}
      />
      <ModalEditProduct
        isOpen={editModal.isOpen}
        toggle={editModal.toggleModal}
        row={activeRow}
      />
      <ModalInfoSubItem
        isOpen={infoSubItemModal.isOpen}
        toggle={infoSubItemModal.toggleModal}
        row={activeRow}
      />
    </div>
  )
}

const MenuSubitem = ({
  onOrderConfigClick,
  onSplitCaseClick,
  onAddSubItemsClick,
  onEditProductClick,
  onDeleteProductClick,
}) => (
  <div className='menu-subitem'>
    <div className='menu-item' onClick={onEditProductClick}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
      <span>Edit Product</span>
    </div>
    <div className='menu-item' onClick={onOrderConfigClick}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="1"></rect><path d="M16 8h4a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-4"></path></svg>
      <span>Order Settings</span>
    </div>
    <div className='menu-item' onClick={onSplitCaseClick}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path></svg>
      <span>Split Case Purchase</span>
    </div>
    <div className='menu-item' onClick={onAddSubItemsClick}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
      <span>Add Sub-items</span>
    </div>
    <div className='menu-item text-delete' onClick={onDeleteProductClick}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path></svg>
      <span>Delete</span>
    </div>
  </div>
)

const AddedOptionInfo = ({ onOpenInfoSubItem, row }) => {
  const hasSubItems = row?.subItems?.length > 0
  const hasSplitCase = Boolean(row?.splitCasePurchase?.itemsPerCase)

  if (!hasSubItems && !hasSplitCase) return '—'

  return (
    <>
      {hasSubItems && (
        <div
          className='sub-items-badge'
          onClick={() => onOpenInfoSubItem(row.id)}
        >
          {row.subItems.length} sub-items
        </div>
      )}
      {hasSplitCase && (
        <div className='sub-toggle'>
          <svg
            width='10'
            height='10'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'></path>
          </svg>
          Buy by unit - {row.splitCasePurchase.itemsPerCase}/case
        </div>
      )}
    </>
  )
}
